import React, { useEffect, useState } from 'react';
import {
  Table,
  Card,
  Typography,
  Tag,
  Space,
  Button,
  message,
  Modal,
  Form,
  Input,
  Select,
  Alert,
} from 'antd';
import { EyeOutlined, ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
};

const { Title, Text } = Typography;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 24px !important;
    font-weight: 600 !important;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 20px !important;
    }
  }
`;

const TableScrollWrapper = styled.div`
  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin: 0 -8px;
    padding: 0 8px;
  }
`;

const MerchantUpdateRequests: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [requestTypeFilter, setRequestTypeFilter] = useState<string>(searchParams.get('requestType') || '');
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);
  const [actionType, setActionType] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [actionForm] = Form.useForm();

  const pageId = parseInt(searchParams.get('pageId') || '1');
  const perPage = parseInt(searchParams.get('perPage') || '10');

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, perPage, statusFilter, requestTypeFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const params: any = {
        pageId,
        perPage,
        sort: 'DESC',
      };
      if (statusFilter) params.status = statusFilter;
      if (requestTypeFilter) params.requestType = requestTypeFilter;

      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_GET_MERCHANT_UPDATE_REQUESTS || '/api/Administrator/merchant-update-requests',
        { params }
      );

      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        setRequests(data.itemsList || []);
        const paginator = data.paginator;
        if (paginator) {
          setPagination({
            current: paginator.currentPage || pageId,
            pageSize: paginator.perPage || perPage,
            total: paginator.itemCount || 0,
          });
        }
      }
    } catch (error: any) {
      console.error('Error fetching merchant update requests:', error);
      message.error(error.response?.data?.message || 'Failed to fetch update requests');
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination: any) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('pageId', newPagination.current.toString());
    newParams.set('perPage', newPagination.pageSize.toString());
    setSearchParams(newParams);
  };

  const updateURLParams = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
      else newParams.delete(key);
    });
    newParams.set('pageId', '1');
    setSearchParams(newParams);
  };

  const handleFilterChange = (key: string, value: string | null) => {
    updateURLParams({ [key]: value });
    if (key === 'status') setStatusFilter(value || '');
    if (key === 'requestType') setRequestTypeFilter(value || '');
  };

  const openDetailModal = (record: any) => {
    setSelectedRequest(record);
    setDetailModalVisible(true);
  };

  const openActionModal = (record: any, type: 'APPROVE' | 'REJECT') => {
    setSelectedRequest(record);
    setActionType(type);
    actionForm.resetFields();
    setActionModalVisible(true);
  };

  const handleActionSubmit = async () => {
    if (!selectedRequest || !actionType) return;

    try {
      const values = await actionForm.validateFields();
      const endpoint =
        actionType === 'APPROVE'
          ? (API_ENDPOINTS.ADMIN_APPROVE_MERCHANT_UPDATE_REQUEST ||
              '/api/Administrator/merchant-update-requests/{id}/approve')
          : (API_ENDPOINTS.ADMIN_REJECT_MERCHANT_UPDATE_REQUEST ||
              '/api/Administrator/merchant-update-requests/{id}/reject');

      await apiClient.post(endpoint.replace('{id}', selectedRequest.id), {
        adminComment: values.adminComment || '',
      });

      message.success(
        actionType === 'APPROVE'
          ? 'Request approved and applied successfully'
          : 'Request rejected'
      );
      setActionModalVisible(false);
      setSelectedRequest(null);
      setActionType(null);
      fetchRequests();
    } catch (error: any) {
      if (!error?.response) {
        // validation error from form, ignore
        return;
      }
      message.error(error.response?.data?.message || 'Failed to process request');
    }
  };

  const columns = [
    {
      title: 'Company',
      dataIndex: ['merchantCompany', 'name'],
      key: 'merchantCompany',
      render: (name: string) => name || '-',
    },
    {
      title: 'Requested By',
      dataIndex: ['requestedByMerchant', 'email'],
      key: 'requestedByMerchant',
      render: (_: any, record: any) =>
        record.requestedByMerchant?.email || '-',
    },
    {
      title: 'Type',
      dataIndex: 'requestType',
      key: 'requestType',
      render: (type: string) => (
        <Tag color="blue">{type || 'UNKNOWN'}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color =
          status === 'APPROVED'
            ? 'green'
            : status === 'REJECTED'
            ? 'red'
            : 'gold';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) =>
        value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openDetailModal(record)}
          >
            View
          </Button>
          {record.status === 'PENDING' && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => openActionModal(record, 'APPROVE')}
              >
                Approve
              </Button>
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => openActionModal(record, 'REJECT')}
              >
                Reject
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, marginBottom: 0 }}>
          Merchant Requests
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchRequests}
          >
            Refresh
          </Button>
        </Space>
      </HeaderContainer>

      <Card
        style={{
          marginBottom: 24,
          background: COLORS.bgSecondary,
          borderColor: COLORS.border,
        }}
      >
        <Space wrap>
          <Space direction="vertical" size={4}>
            <Text style={{ color: COLORS.textSecondary }}>Status</Text>
            <Select
              allowClear
              placeholder="All"
              value={statusFilter || undefined}
              onChange={(value) => handleFilterChange('status', value || null)}
              style={{ width: 160 }}
            >
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="APPROVED">Approved</Select.Option>
              <Select.Option value="REJECTED">Rejected</Select.Option>
            </Select>
          </Space>
          <Space direction="vertical" size={4}>
            <Text style={{ color: COLORS.textSecondary }}>Request Type</Text>
            <Select
              allowClear
              placeholder="All"
              value={requestTypeFilter || undefined}
              onChange={(value) =>
                handleFilterChange('requestType', value || null)
              }
              style={{ width: 240 }}
            >
              <Select.Option value="CUSTODIAL_WALLET_CHANGE">
                Custodial Wallet Change
              </Select.Option>
              <Select.Option value="UNKNOWN_TOKEN_SWEEP_REQUEST">
                Unknown Token Sweep Request
              </Select.Option>
            </Select>
          </Space>
        </Space>
      </Card>

      <Card
        style={{
          background: COLORS.bgSecondary,
          borderColor: COLORS.border,
        }}
      >
        <TableScrollWrapper>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={requests}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: window.innerWidth > 768,
              showTotal: (total, range) => (
                <span style={{ color: COLORS.textSecondary }}>
                  Showing {range[0]}-{range[1]} of {total} requests
                </span>
              ),
              pageSizeOptions: ['10', '20', '50', '100'],
            }}
            onChange={handleTableChange}
            style={{ background: 'transparent' }}
            size="small"
            scroll={{ x: 'max-content' }}
          />
        </TableScrollWrapper>
      </Card>

      <Modal
        title="Request Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={720}
      >
        {selectedRequest && (
          <div style={{ color: COLORS.textPrimary }}>
            <p>
              <strong>Company:</strong>{' '}
              {selectedRequest.merchantCompany?.name || '-'}
            </p>
            <p>
              <strong>Requested By:</strong>{' '}
              {selectedRequest.requestedByMerchant?.email || '-'}
            </p>
            <p>
              <strong>Type:</strong> {selectedRequest.requestType}
            </p>
            <p>
              <strong>Status:</strong> {selectedRequest.status}
            </p>
            <p>
              <strong>Payload Before:</strong>
            </p>
            <pre style={{ background: '#111827', padding: 12, borderRadius: 6 }}>
              <code>
                {JSON.stringify(selectedRequest.payloadBefore, null, 2)}
              </code>
            </pre>
            <p>
              <strong>Payload After:</strong>
            </p>
            <pre style={{ background: '#111827', padding: 12, borderRadius: 6 }}>
              <code>
                {JSON.stringify(selectedRequest.payloadAfter, null, 2)}
              </code>
            </pre>
            {selectedRequest.adminComment && (
              <p>
                <strong>Admin Comment:</strong> {selectedRequest.adminComment}
              </p>
            )}
            {selectedRequest.processedAt && (
              <p>
                <strong>Processed At:</strong>{' '}
                {dayjs(selectedRequest.processedAt).format(
                  'YYYY-MM-DD HH:mm'
                )}
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={actionType === 'APPROVE' ? 'Approve Request' : 'Reject Request'}
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          setActionType(null);
          setSelectedRequest(null);
          actionForm.resetFields();
        }}
        onOk={handleActionSubmit}
        okText={actionType === 'APPROVE' ? 'Approve' : 'Reject'}
        okButtonProps={{
          type: actionType === 'APPROVE' ? 'primary' : 'primary',
          danger: actionType === 'REJECT',
        }}
        width={600}
      >
        {actionType === 'APPROVE' && selectedRequest?.requestType === 'UNKNOWN_TOKEN_SWEEP_REQUEST' && (
          <Alert
            message="⚠️ Security Warning: Contract Address Verification Required"
            description={
              <div>
                <p style={{ marginBottom: 8, fontWeight: 500 }}>
                  Before approving this unknown token sweep request, you MUST verify that the contract address is safe and not harmful.
                </p>
                <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                  <li>Check the contract address on blockchain explorers (Etherscan, BscScan, etc.)</li>
                  <li>Verify the contract is not a known scam, honeypot, or malicious contract</li>
                  <li>Confirm the contract address matches the expected token contract</li>
                  <li>Review the contract code if possible to ensure it's legitimate</li>
                </ul>
                <p style={{ marginTop: 12, marginBottom: 0, color: '#ff4d4f', fontWeight: 500 }}>
                  Approving a malicious contract could result in loss of funds or security risks.
                </p>
              </div>
            }
            type="error"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}
        <Form form={actionForm} layout="vertical">
          <Form.Item
            name="adminComment"
            label="Admin Comment"
            extra="Optional note explaining your decision"
          >
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default MerchantUpdateRequests;


